import React, { useRef, useMemo } from "react";
import {
  Box,
  IconButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ClientCarousel.css";

const ClientCarousel = ({ 
  clients = [], 
  autoplay = true, 
  autoplaySpeed = 3000,
  showArrows = true,
  showDots = true // Enable dots by default
}) => {
  const sliderRef = useRef(null);

  // Responsive breakpoint values for slides to show in each group
  const slidesToShow = useBreakpointValue({
    base: 2,  
    sm: 3,    
    md: 4,     
    lg: 6,    
    xl: 8,    
    "2xl": 10  
  });

  // Create groups of logos based on screen size for fade effect
  const logoGroups = useMemo(() => {
    if (!clients?.length || !slidesToShow) return [];
    
    const groups = [];
    for (let i = 0; i < clients.length; i += slidesToShow) {
      groups.push(clients.slice(i, i + slidesToShow));
    }
    return groups;
  }, [clients, slidesToShow]);

  const sliderSettings = useMemo(() => ({
    dots: showDots,
    infinite: true,
    speed: 800,
    fade: false, // Disable default fade
    cssEase: "ease-in-out",
    slidesToShow: 1, // Show 1 group at a time
    slidesToScroll: 1, // Scroll 1 group at a time
    autoplay: autoplay,
    autoplaySpeed: autoplaySpeed,
    arrows: false, 
    pauseOnHover: true,
    swipeToSlide: true,
    touchMove: true,
    responsive: [
      {
        breakpoint: 9999, // Apply to all screen sizes
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          fade: true, // Enable fade for single group view
        }
      }
    ]
  }), [autoplay, autoplaySpeed, showDots]);

  const handlePrevious = () => sliderRef.current?.slickPrev();
  const handleNext = () => sliderRef.current?.slickNext();

  if (!clients?.length) return null;

  const shouldShowArrows = showArrows && logoGroups.length > 1;

  return (
    <Box 
      position="relative" 
      w="full" 
      py={4}
      px={{ base: 16, sm: 20, md: 24, lg: 28 }} // Increased padding to accommodate arrows
    >
      {shouldShowArrows && (
        <NavigationArrows 
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      )}

      <CarouselContainer>
        <Slider ref={sliderRef} {...sliderSettings}>
          {logoGroups.map((group, groupIndex) => (
            <Box key={groupIndex} px={2}>
              <Box
                display="grid"
                gridTemplateColumns={`repeat(${Math.min(group.length, slidesToShow)}, 1fr)`}
                gap={4}
                w="full"
                h="140px"
                alignItems="center"
                justifyContent="center"
              >
                {group.map((client, clientIndex) => (
                  <ClientLogoSlide
                    key={client.id || `${groupIndex}-${clientIndex}`}
                    client={client}
                    index={clientIndex}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Slider>
      </CarouselContainer>
    </Box>
  );
};

// Separated Navigation Arrows Component for cleaner code
const NavigationArrows = ({ onPrevious, onNext }) => {
  const arrowStyles = {
    position: "absolute",
    top: "50%",
    zIndex: 2,
    bg: "white",
    border: "2px solid #000B8C",
    color: "#000B8C",
    borderRadius: "50%", // Make perfectly circular
    w: { base: "40px", md: "48px", lg: "56px" }, // Set width explicitly
    h: { base: "40px", md: "48px", lg: "56px" }, // Set height explicitly
    minW: { base: "40px", md: "48px", lg: "56px" }, // Prevent shrinking
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    _hover: {
      bg: "#000B8C",
      color: "white",
      transform: "translateY(-50%) scale(1.1)",
    },
    _active: {
      transform: "translateY(-50%) scale(0.95)",
    }
  };

  return (
    <>
      <IconButton
        aria-label="Previous slide"
        icon={<ChevronLeftIcon boxSize={{ base: 4, md: 5, lg: 6 }} />}
        left={{ base: "8px", sm: "12px", md: "16px", lg: "20px" }}
        transform="translateY(-50%)"
        onClick={onPrevious}
        {...arrowStyles}
      />
      <IconButton
        aria-label="Next slide"
        icon={<ChevronRightIcon boxSize={{ base: 4, md: 5, lg: 6 }} />}
        right={{ base: "8px", sm: "12px", md: "16px", lg: "20px" }}
        transform="translateY(-50%)"
        onClick={onNext}
        {...arrowStyles}
      />
    </>
  );
};

// Separated Carousel Container Component
const CarouselContainer = ({ children }) => (
  <Box 
    className="client-carousel-container"
    sx={{
      ".slick-track": {
        display: "flex",
        alignItems: "center",
      },
      ".slick-slide": {
        display: "flex !important",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 4px",
      },
      ".slick-dots": {
        bottom: "-50px",
        "li button:before": {
          color: "#000B8C",
          fontSize: "12px",
        },
        "li.slick-active button:before": {
          color: "#000B8C",
        },
      },
    }}
  >
    {children}
  </Box>
);

// Separated Client Logo Slide Component
const ClientLogoSlide = ({ client, index }) => (
  <Box
    h="120px"
    w="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    transition="all 0.3s ease"
    cursor="pointer"
    _hover={{
      transform: "scale(1.05)",
      boxShadow: "0 10px 25px rgba(0, 11, 140, 0.15)",
    }}
    _active={{
      transform: "scale(0.98)",
    }}
  >
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      <img
        src={client.logo || client.url || "/default-logo.png"}
        alt={client.name || client.alt || `Client ${index + 1}`}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          transition: "all 0.3s ease",
        }}
        loading="lazy"
      />
    </Box>
  </Box>
);

export default ClientCarousel;